import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import showToast from '@/components/ui/Toast';
import {
  getVouchers,
  updateVoucherStatus,
  deleteVoucher,
  VoucherItem,
} from '@/services/voucherService';
import { getAdminBasePath } from '@/utils/adminPaths';

type FilterTab = 'all' | 'running' | 'scheduled' | 'expired';

const formatVoucherDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '-';
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'Mei',
    'Jun',
    'Jul',
    'Agu',
    'Sep',
    'Okt',
    'Nov',
    'Des',
  ];
  return `${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
};

const getVoucherStatusCategory = (voucher: VoucherItem): 'running' | 'scheduled' | 'expired' => {
  if (!voucher.is_active) return 'expired';
  const now = new Date();
  const from = new Date(voucher.valid_from);
  const until = new Date(voucher.valid_until);

  if (!isNaN(from.getTime()) && now < from) {
    return 'scheduled';
  }
  if (!isNaN(until.getTime()) && now > until) {
    return 'expired';
  }
  return 'running';
};

const parseApplicableCategories = (voucher: VoucherItem): string[] => {
  // Try parsing terms_and_conditions first if it stores specific tags
  if (voucher.terms_and_conditions) {
    try {
      const parsed = JSON.parse(voucher.terms_and_conditions);
      if (Array.isArray(parsed?.categories) && parsed.categories.length > 0) {
        return parsed.categories;
      }
    } catch {
      // If terms_and_conditions is comma-separated
      const items = voucher.terms_and_conditions.split(',').map((s) => s.trim().toLowerCase());
      const tags: string[] = [];
      if (items.includes('arena') || items.includes('lapangan')) tags.push('Lapangan');
      if (items.includes('service') || items.includes('sewa')) tags.push('Sewa & Layanan');
      if (items.includes('product') || items.includes('produk')) tags.push('Produk');
      if (tags.length > 0) return tags;
    }
  }

  // Fallback to applicable_for
  const app = voucher.applicable_for;
  if (app === 'arena') return ['Lapangan'];
  if (app === 'products') return ['Produk'];
  if (app === 'both') return ['Lapangan', 'Sewa & Layanan', 'Produk'];
  return ['Lapangan'];
};

const VoucherList: React.FC = () => {
  const navigate = useNavigate();
  const adminBase = getAdminBasePath();

  const [vouchers, setVouchers] = useState<VoucherItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const pageSize = 10;

  // Toggle & delete states
  const [togglingCode, setTogglingCode] = useState<string | null>(null);
  const [deletingVoucher, setDeletingVoucher] = useState<VoucherItem | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const fetchVouchers = async () => {
    try {
      setLoading(true);
      const res = await getVouchers(1, 100);
      if (res && res.data && Array.isArray(res.data.data)) {
        setVouchers(res.data.data);
      } else {
        setVouchers([]);
      }
    } catch (err) {
      console.error('Error fetching vouchers:', err);
      showToast.error('Gagal memuat data voucher');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchVouchers();
  }, []);

  // Compute status counts
  const counts = useMemo(() => {
    let running = 0;
    let scheduled = 0;
    let expired = 0;

    vouchers.forEach((v) => {
      const cat = getVoucherStatusCategory(v);
      if (cat === 'running') running++;
      else if (cat === 'scheduled') scheduled++;
      else if (cat === 'expired') expired++;
    });

    return {
      all: vouchers.length,
      running,
      scheduled,
      expired,
    };
  }, [vouchers]);

  // Filter vouchers based on search & active tab
  const filteredVouchers = useMemo(() => {
    return vouchers.filter((v) => {
      // Status filter
      if (activeTab !== 'all') {
        const cat = getVoucherStatusCategory(v);
        if (cat !== activeTab) return false;
      }

      // Search filter
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase().trim();
        const codeMatch = v.voucher_code?.toLowerCase().includes(q);
        const nameMatch = v.voucher_name?.toLowerCase().includes(q);
        const descMatch = v.description?.toLowerCase().includes(q);
        if (!codeMatch && !nameMatch && !descMatch) return false;
      }

      return true;
    });
  }, [vouchers, activeTab, searchQuery]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredVouchers.length / pageSize));
  const paginatedVouchers = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredVouchers.slice(start, start + pageSize);
  }, [filteredVouchers, currentPage]);

  const handleToggleStatus = async (item: VoucherItem) => {
    const nextState = !item.is_active;
    setTogglingCode(item.voucher_code);

    try {
      const res = await updateVoucherStatus(item.voucher_code, nextState);
      if (res.success) {
        setVouchers((prev) =>
          prev.map((v) => (v.voucher_code === item.voucher_code ? { ...v, is_active: nextState } : v))
        );
        showToast.success(`Voucher ${item.voucher_code} ${nextState ? 'diaktifkan' : 'dinonaktifkan'}`);
      } else {
        showToast.error(res.message || 'Gagal memperbarui status voucher');
      }
    } catch (err: any) {
      console.error(err);
      showToast.error(err?.message || 'Gagal memperbarui status voucher');
    } finally {
      setTogglingCode(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deletingVoucher) return;
    setIsDeleting(true);

    try {
      const res = await deleteVoucher(deletingVoucher.voucher_code);
      if (res.success) {
        setVouchers((prev) => prev.filter((v) => v.voucher_code !== deletingVoucher.voucher_code));
        showToast.success(`Voucher "${deletingVoucher.voucher_code}" berhasil dihapus`);
        setDeletingVoucher(null);
      } else {
        showToast.error(res.message || 'Gagal menghapus voucher');
      }
    } catch (err: any) {
      console.error(err);
      showToast.error(err?.message || 'Gagal menghapus voucher');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6 max-w-8xl mx-auto">
      {/* ============================================================== */}
      {/* HEADER: TITLE & TAMBAH VOUCHER BUTTON                         */}
      {/* ============================================================== */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-[#0E1726]">Voucher</h1>
        </div>

        <button
          type="button"
          onClick={() => navigate(`${adminBase}/voucher/create`)}
          className="inline-flex items-center justify-center gap-1.5 rounded-full bg-[#FC480D] hover:bg-[#E8490F] text-white text-sm font-semibold px-5 py-2.5 shadow-xs transition-colors cursor-pointer self-start sm:self-auto"
        >
          <Icon icon="ph:plus-bold" className="w-4 h-4" />
          <span>Tambah Voucher</span>
        </button>
      </div>

      {/* ============================================================== */}
      {/* MAIN CARD CONTAINER (Matched with ServiceList)                 */}
      {/* ============================================================== */}
      <div className="rounded-2xl border border-[#E0E6ED] bg-white shadow-xs overflow-hidden">
        {/* Card Header Toolbar: Search & Status Filters */}
        <div className="p-4 sm:p-5 flex flex-col md:flex-row md:items-center justify-between gap-4">
          {/* SEARCH BAR */}
          <div className="relative w-full sm:w-72">
            <Icon
              icon="ph:magnifying-glass"
              className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setCurrentPage(1);
              }}
              placeholder="Cari kode voucher"
              className="w-full rounded-full border border-[#E0E6ED] bg-white pl-10 pr-4 py-2 text-xs sm:text-sm text-[#0E1726] placeholder:text-gray-400 focus:border-[#FC480D] focus:outline-none transition-colors"
            />
          </div>

          {/* FILTER PILLS */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0 scrollbar-none">
            <button
              type="button"
              onClick={() => {
                setActiveTab('all');
                setCurrentPage(1);
              }}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'all'
                  ? 'border border-[#FC480D] bg-white text-[#FC480D]'
                  : 'border border-[#E0E6ED] bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Semua {counts.all}
            </button>

            <button
              type="button"
              onClick={() => {
                setActiveTab('running');
                setCurrentPage(1);
              }}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'running'
                  ? 'border border-[#FC480D] bg-white text-[#FC480D]'
                  : 'border border-[#E0E6ED] bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Berjalan {counts.running}
            </button>

            <button
              type="button"
              onClick={() => {
                setActiveTab('scheduled');
                setCurrentPage(1);
              }}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'scheduled'
                  ? 'border border-[#FC480D] bg-white text-[#FC480D]'
                  : 'border border-[#E0E6ED] bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Terjadwal {counts.scheduled}
            </button>

            <button
              type="button"
              onClick={() => {
                setActiveTab('expired');
                setCurrentPage(1);
              }}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${
                activeTab === 'expired'
                  ? 'border border-[#FC480D] bg-white text-[#FC480D]'
                  : 'border border-[#E0E6ED] bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              Berakhir {counts.expired}
            </button>
          </div>
        </div>

        {/* Content area: Loading / Empty / Table */}
        {loading ? (
          <div className="py-20 flex flex-col items-center justify-center gap-3">
            <Icon icon="ph:spinner" className="w-8 h-8 text-[#FC480D] animate-spin" />
            <span className="text-sm text-gray-500">Memuat data voucher...</span>
          </div>
        ) : filteredVouchers.length === 0 ? (
          <div className="py-16 text-center space-y-3 px-4">
            <div className="w-14 h-14 rounded-full bg-orange-50 border border-orange-100 flex items-center justify-center mx-auto text-[#FC480D]">
              <Icon icon="ph:ticket-bold" className="w-7 h-7" />
            </div>
            <h3 className="text-sm font-semibold text-[#0E1726]">Belum ada voucher</h3>
            <p className="text-xs text-[#8C8C8C] max-w-xs mx-auto">
              Tambahkan voucher diskon untuk menarik lebih banyak pelanggan dan meningkatkan transaksi arena Anda.
            </p>
            <button
              type="button"
              onClick={() => navigate(`${adminBase}/voucher/create`)}
              className="mt-2 rounded-full bg-[#FC480D] hover:bg-[#E8490F] text-white px-5 py-2 text-xs font-semibold transition-colors inline-flex items-center gap-1.5 cursor-pointer"
            >
              <Icon icon="ph:plus-bold" className="w-3.5 h-3.5" />
              <span>Tambah Sekarang</span>
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] border-collapse text-left">
              <thead>
                <tr className="bg-[#F8F9FA] border-y border-[#F0F2F5] text-xs font-semibold text-[#0E1726]">
                  <th className="py-3 px-6 text-left font-semibold">Voucher</th>
                  <th className="py-3 px-6 text-left font-semibold">Diskon</th>
                  <th className="py-3 px-6 text-left font-semibold">Berlaku untuk</th>
                  <th className="py-3 px-6 text-left font-semibold">Periode</th>
                  <th className="py-3 px-6 text-left font-semibold">Pemakaian</th>
                  <th className="py-3 px-6 text-center font-semibold">Status</th>
                  <th className="py-3 px-6 text-right font-semibold">Aksi</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-[#F0F2F5]">
                {paginatedVouchers.map((item) => {
                  const statusCat = getVoucherStatusCategory(item);
                  const categories = parseApplicableCategories(item);

                  return (
                    <tr key={item.voucher_code} className="hover:bg-gray-50/70 transition-colors">
                      {/* VOUCHER CODE & DESCRIPTION */}
                      <td className="py-4 px-6 align-middle max-w-[260px]">
                        <div>
                          <span className="border border-dashed border-[#FC480D] bg-orange-50/40 text-[#FC480D] font-semibold text-xs px-2.5 py-1 rounded-md tracking-wider inline-block">
                            {item.voucher_code}
                          </span>
                          {item.description ? (
                            <p className="text-xs text-[#8C8C8C] mt-1 line-clamp-1">
                              {item.description}
                            </p>
                          ) : (
                            <p className="text-xs text-gray-400 italic mt-1">
                              Tanpa deskripsi
                            </p>
                          )}
                        </div>
                      </td>

                      {/* DISKON */}
                      <td className="py-4 px-6 align-middle whitespace-nowrap">
                        <span className="font-semibold text-[#0E1726] text-sm">
                          {item.discount_type === 'percentage'
                            ? `${item.discount_value}%`
                            : `Rp. ${Number(item.discount_value || 0).toLocaleString('id-ID')}`}
                        </span>
                      </td>

                      {/* BERLAKU UNTUK */}
                      <td className="py-4 px-6 align-middle">
                        <div className="flex flex-wrap gap-1.5 items-center">
                          {categories.map((cat, idx) => (
                            <span
                              key={idx}
                              className="bg-[#F5F6F7] text-gray-700 text-xs px-2.5 py-0.5 rounded-md font-medium inline-block"
                            >
                              {cat}
                            </span>
                          ))}
                        </div>
                      </td>

                      {/* PERIODE */}
                      <td className="py-4 px-6 align-middle whitespace-nowrap">
                        <span
                          className={`font-semibold text-xs sm:text-sm ${
                            statusCat === 'running'
                              ? 'text-[#0BCF46]'
                              : 'text-red-500'
                          }`}
                        >
                          {formatVoucherDate(item.valid_from)} – {formatVoucherDate(item.valid_until)}
                        </span>
                      </td>

                      {/* PEMAKAIAN */}
                      <td className="py-4 px-6 align-middle whitespace-nowrap">
                        <div className="text-xs sm:text-sm">
                          <span className="font-semibold text-[#0E1726]">{item.used_count || 0}</span>
                          <span className="text-[#8C8C8C] font-normal">
                            {' '}
                            / {item.usage_limit && item.usage_limit > 0 ? item.usage_limit : '∞'}
                          </span>
                        </div>
                      </td>

                      {/* STATUS TOGGLE */}
                      <td className="py-4 px-6 align-middle text-center whitespace-nowrap">
                        <div className="inline-flex items-center gap-2">
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              disabled={togglingCode === item.voucher_code}
                              checked={item.is_active}
                              onChange={() => handleToggleStatus(item)}
                              className="sr-only peer"
                            />
                            <div className="w-10 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#FC480D]"></div>
                          </label>
                          <span
                            className={`text-xs font-semibold ${
                              item.is_active ? 'text-[#0E1726]' : 'text-gray-400'
                            }`}
                          >
                            {item.is_active ? 'Aktif' : 'Nonaktif'}
                          </span>
                        </div>
                      </td>

                      {/* AKSI */}
                      <td className="py-4 px-6 align-middle text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => navigate(`${adminBase}/voucher/edit/${item.voucher_code}`)}
                            title="Edit voucher"
                            aria-label={`Edit ${item.voucher_code}`}
                            className="w-8 h-8 rounded-full border border-[#E0E6ED] bg-white hover:bg-gray-50 text-gray-600 hover:text-[#FC480D] flex items-center justify-center transition-colors shadow-2xs cursor-pointer"
                          >
                            <Icon icon="ph:pencil-simple" className="w-4 h-4" />
                          </button>

                          <button
                            type="button"
                            onClick={() => setDeletingVoucher(item)}
                            title="Hapus voucher"
                            aria-label={`Hapus ${item.voucher_code}`}
                            className="w-8 h-8 rounded-full border border-[#E0E6ED] bg-white hover:bg-red-50 text-gray-600 hover:text-red-600 flex items-center justify-center transition-colors shadow-2xs cursor-pointer"
                          >
                            <Icon icon="ph:trash" className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Card Footer */}
        {filteredVouchers.length > 0 && (
          <div className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-t border-[#F0F2F5]">
            <span className="text-xs text-[#8C8C8C]">
              Menampilkan {Math.min((currentPage - 1) * pageSize + 1, filteredVouchers.length)} -{' '}
              {Math.min(currentPage * pageSize, filteredVouchers.length)} dari {filteredVouchers.length} voucher
            </span>

            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                  className="rounded-full border border-[#E0E6ED] bg-white hover:bg-gray-50 disabled:opacity-40 text-xs font-semibold text-gray-700 px-3.5 py-1.5 transition-colors cursor-pointer"
                >
                  Sebelumnya
                </button>
                <div className="flex items-center gap-1.5">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      type="button"
                      onClick={() => setCurrentPage(page)}
                      className={`w-7 h-7 rounded-full font-semibold text-xs flex items-center justify-center transition-colors cursor-pointer ${
                        currentPage === page
                          ? 'bg-[#FC480D] text-white shadow-xs'
                          : 'border border-[#E0E6ED] text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                  className="rounded-full border border-[#E0E6ED] bg-white hover:bg-gray-50 disabled:opacity-40 text-xs font-semibold text-gray-700 px-3.5 py-1.5 transition-colors cursor-pointer"
                >
                  Selanjutnya
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ============================================================== */}
      {/* MODAL: KONFIRMASI HAPUS (Screenshot 2)                         */}
      {/* ============================================================== */}
      {deletingVoucher && (
        <div
          onClick={() => {
            if (!isDeleting) {
              setDeletingVoucher(null);
            }
          }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-xs !mt-0"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl max-w-[420px] w-full p-6 sm:p-7 shadow-2xl relative text-left animate-in fade-in zoom-in-95"
          >
            <h3 className="text-base sm:text-lg font-semibold text-[#0E1726]">
              Hapus voucher {deletingVoucher.voucher_code}?
            </h3>
            <p className="text-xs sm:text-sm text-[#8C8C8C] leading-relaxed mt-2.5">
              Voucher tidak bisa dipakai lagi. Transaksi yang sudah memakai voucher ini tetap tersimpan.
            </p>

            <div className="flex items-center justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={() => setDeletingVoucher(null)}
                className="rounded-full px-4 py-2 text-xs sm:text-sm font-semibold text-[#FC480D] hover:bg-orange-50/60 transition-colors cursor-pointer"
              >
                Batal
              </button>

              <button
                type="button"
                disabled={isDeleting}
                onClick={handleConfirmDelete}
                className="rounded-full bg-[#E53935] hover:bg-red-700 disabled:opacity-50 text-white font-semibold text-xs sm:text-sm px-6 py-2.5 shadow-xs transition-colors cursor-pointer flex items-center gap-1.5"
              >
                {isDeleting && <Icon icon="ph:spinner" className="w-4 h-4 animate-spin" />}
                <span>Hapus</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VoucherList;
